import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { useSimpleAuth } from '../contexts/SimpleAuthContext';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
}

interface ChatInterfaceProps {
  context: any; // Test data context
  viewType: 'single' | 'trends';
  selectedDate?: string;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ context, viewType, selectedDate }) => {
  const { user } = useSimpleAuth();
  
  // Debug the props being passed to ChatInterface (only when chat is opened)
  React.useEffect(() => {
    console.log('=== CHAT INTERFACE MOUNTED ===');
    console.log('Context keys:', Object.keys(context || {}));
    console.log('View type:', viewType);
    console.log('Has user:', !!user);
  }, []);  // Only run on mount
  
  // Generate initial message based on available context
  const getInitialMessage = () => {
    const testNames = Object.keys(context || {});
    const testCount = testNames.length;
    
    // Generate appropriate message based on available data
    
    if (testCount === 0) {
      return `Welcome! I'm here to help you understand your lab results.

To get started:
1. Upload a lab report PDF using the upload button
2. Extract and review your lab values
3. Then ask me questions about your results!

I can help explain what different tests mean, whether your values are normal, and provide general wellness information.`;
    }
    
    const testList = testNames.map(name => {
      const testInfo = context[name];
      return testInfo?.displayName || name;
    }).join(', ');
    
    let message = `Welcome! I can help you understand your lab results. `;
    
    if (viewType === 'single' && selectedDate) {
      message += `I'm looking at your test results from ${new Date(selectedDate).toLocaleDateString()}. `;
    } else if (viewType === 'trends') {
      message += `I can see your test trends over time. `;
    }
    
    message += `\n\nAvailable tests: ${testList}\n\nFeel free to ask me about:\n• Specific test values and what they mean\n• Whether your results are within normal ranges\n• General questions about improving your health\n• What trends in your results might indicate`;
    
    return message;
  };
  
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: getInitialMessage(),
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Only scroll to bottom for user messages, not assistant responses
  const scrollToBottom = (forceScroll = false) => {
    if (forceScroll) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  };

  // Don't auto-scroll on message updates - let user control their view

  // Update initial message when context changes
  useEffect(() => {
    if (messages.length === 1 && messages[0].id === '1') {
      // Only update if we still have just the initial message
      setMessages([{
        id: '1',
        role: 'assistant',
        content: getInitialMessage(),
        timestamp: new Date()
      }]);
    }
  }, [context, viewType, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    // Scroll to bottom when user sends a message
    scrollToBottom(true);

    // Add loading message
    const loadingMessage: Message = {
      id: Date.now().toString() + '-loading',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      console.log('=== HANDLE SEND DEBUG ===');
      console.log('User message:', userMessage.content);
      console.log('Context keys:', Object.keys(context || {}));
      console.log('User ID:', user?.id);
      
      // Prepare context for the API
      const apiContext = {
        viewType,
        selectedDate,
        testData: context,
        question: userMessage.content
      };

      console.log('About to call generateIntelligentResponse...');
      // Generate intelligent response using context and LLM
      const response = await generateIntelligentResponse(
        userMessage.content,
        context,
        viewType,
        selectedDate,
        messages,
        user?.id?.toString()
      );
      
      console.log('=== RESPONSE RECEIVED ===');
      console.log('Response type:', typeof response);
      console.log('Response length:', response?.length);
      console.log('Response content:', response);
      
      if (!response || response.trim() === '') {
        console.error('Empty response received!');
        console.error('Raw response:', response);
        console.error('Response type:', typeof response);
        const errorMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: 'I apologize, but I received an empty response. Please try asking your question again.',
          timestamp: new Date()
        };
        setMessages(prev => prev.filter(m => !m.isLoading).concat(errorMessage));
        return;
      }
      
      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };

      console.log('=== SETTING ASSISTANT MESSAGE ===');
      console.log('Assistant message:', assistantMessage);
      
      // Remove loading message and add real response
      setMessages(prev => {
        const filtered = prev.filter(m => !m.isLoading);
        const newMessages = filtered.concat(assistantMessage);
        console.log('New messages array:', newMessages);
        console.log('Assistant message content length:', assistantMessage.content?.length);
        console.log('Assistant message content preview:', assistantMessage.content?.substring(0, 100));
        return newMessages;
      });
    } catch (error) {
      console.error('Chat error:', error);
      
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'I apologize, but I encountered an error while processing your request. Please try again later.',
        timestamp: new Date()
      };

      // Remove loading message and add error
      setMessages(prev => prev.filter(m => !m.isLoading).concat(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [input]);

  return (
    <div className="flex flex-col h-full" style={{ height: '100%' }}>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ minHeight: 0 }}>
        <div className="space-y-4">
          {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              {message.isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              ) : (
                <div className="whitespace-pre-wrap break-words">
                  {message.content || '[Empty response - debugging]'}
                </div>
              )}
              <div className={`text-xs mt-1 ${
                message.role === 'user' ? 'text-blue-200' : 'text-gray-500'
              }`}>
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 px-4 py-3">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your lab results..."
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={1}
            style={{ maxHeight: '120px' }}
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className={`px-4 py-2 rounded-lg transition-colors ${
              !input.trim() || isLoading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          Press Enter to send, Shift+Enter for new line
        </div>
      </div>
    </div>
  );
};

// Generate intelligent response using LLM and context
async function generateIntelligentResponse(
  userMessage: string, 
  context: any, 
  viewType: string, 
  selectedDate: string | undefined,
  messageHistory: Message[],
  sessionId?: string
): Promise<string> {
  const userMessageLower = userMessage.toLowerCase();
  
  console.log('=== CHAT DEBUG ===');
  console.log('Generating response for:', userMessage);
  console.log('Context available:', Object.keys(context || {}));
  console.log('Session ID:', sessionId);
  console.log('View type:', viewType);
  
  try {
    // Check if this is a trend analysis question first
    const isTrendAnalysisQuestion = userMessageLower.includes('trend') || 
      userMessageLower.includes('change') ||
      userMessageLower.includes('improve') ||
      userMessageLower.includes('recommendation') ||
      userMessageLower.includes('getting better') ||
      userMessageLower.includes('getting worse') ||
      userMessageLower.includes('over time') ||
      userMessageLower.includes('pattern') ||
      userMessageLower.includes('progress');
    
    console.log('Is trend analysis question:', isTrendAnalysisQuestion);
    
    // Try trend analysis API for trend-related questions
    if (isTrendAnalysisQuestion && sessionId) {
      try {
        console.log('Trying trend analysis API...');
        const response = await apiService.post('/api/trends/analyze', {
          sessionId,
          testData: context,
          question: userMessage,
          viewType,
          selectedDate
        });
        
        console.log('Trend API response:', response);
        console.log('Trend API response.data:', response.data);
        console.log('Trend API response.analysis:', (response as any).analysis);
        if (response.status === 'success') {
          // The backend returns the analysis as an object in the response
          const analysis = (response as any).analysis || response.data;
          console.log('Trend API success, extracting analysis:', analysis);
          
          if (analysis && typeof analysis === 'object') {
            // Extract text content from the analysis object
            let result = '';
            
            // Add insights (main interpretation)
            if (analysis.insights && typeof analysis.insights === 'string') {
              result += analysis.insights + '\n\n';
            }
            
            // Add clinical significance if present
            if (analysis.clinical_significance && typeof analysis.clinical_significance === 'string') {
              result += 'Clinical Significance: ' + analysis.clinical_significance + '\n\n';
            }
            
            // Add recommendations if present
            if (analysis.recommendations && Array.isArray(analysis.recommendations)) {
              const recommendations = analysis.recommendations.filter((rec: any) => typeof rec === 'string').join('\n• ');
              if (recommendations) {
                result += 'Recommendations:\n• ' + recommendations + '\n\n';
              }
            }
            
            // Fallback to string representation if no text found
            if (!result.trim() && typeof analysis === 'string') {
              result = analysis;
            }
            
            if (result.trim()) {
              return result.trim();
            } else {
              console.warn('Trend API returned analysis object but no extractable text content');
              // Fall through to next API attempt
            }
          } else if (analysis && typeof analysis === 'string') {
            return analysis;
          } else {
            console.warn('Trend API returned success but no analysis content');
            // Fall through to next API attempt
          }
        }
      } catch (trendError: any) {
        console.log('Trend analysis error:', trendError.response?.data || trendError.message);
        console.log('Falling back to public LLM explanation...');
        
        // Create a summary of current lab values for the LLM
        const labSummary = Object.entries(context || {}).slice(0, 5).map(([testName, testInfo]: [string, any]) => {
          if (testInfo.dataPoints && testInfo.dataPoints.length > 0) {
            const latest = testInfo.dataPoints[testInfo.dataPoints.length - 1];
            return `${testInfo.displayName || testName}: ${latest.value} ${latest.units}`;
          }
          return '';
        }).filter(Boolean).join(', ');
        
        console.log('Lab summary for LLM:', labSummary);
        
        if (labSummary) {
          try {
            console.log('Trying LLM fallback for trend question...');
            const llmResponse = await apiService.post('/api/llm_explanation', {
              marker: "Health Analysis",
              value: userMessage,
              units: "Question",
              context: {
                labSummary,
                question: userMessage
              }
            });
            
            console.log('LLM fallback response:', llmResponse);
            if (llmResponse.status === 'success' && llmResponse.data?.explanation) {
              console.log('LLM fallback success, returning:', llmResponse.data.explanation);
              return llmResponse.data.explanation;
            }
          } catch (llmError) {
            console.error('LLM fallback also failed:', llmError);
          }
        }
      }
    }
    
    // Then try the general chat API if available
    try {
      console.log('Trying general chat API...');
      // Include the current message in the conversation
      const allMessages = [
        ...messageHistory.filter(m => !m.isLoading).map(m => ({
          role: m.role,
          content: m.content
        })),
        { role: 'user', content: userMessage }
      ];
      
      console.log('Sending messages to chat API:', allMessages);
      
      const response = await apiService.post('/api/chat', {
        sessionId,
        context: { viewType, selectedDate, testData: context },
        messages: allMessages
      });
      
      console.log('Chat API response:', response);
      if (response.status === 'success') {
        const responseData = response.data as any;
        const result = responseData?.response || responseData?.analysis || responseData;
        console.log('Chat API success, returning:', result);
        if (result && typeof result === 'string') {
          return result;
        } else {
          console.warn('Chat API returned success but no response content');
          // Fall through to next API attempt
        }
      }
    } catch (chatError: any) {
      console.log('Chat error details:', chatError.response?.data || chatError.message);
      console.log('Chat API not available, trying public LLM endpoint...');
      
      // Try the public LLM explanation endpoint as final fallback
      const labSummary = Object.entries(context || {}).slice(0, 5).map(([testName, testInfo]: [string, any]) => {
        if (testInfo.dataPoints && testInfo.dataPoints.length > 0) {
          const latest = testInfo.dataPoints[testInfo.dataPoints.length - 1];
          return `${testInfo.displayName || testName}: ${latest.value} ${latest.units}`;
        }
        return '';
      }).filter(Boolean).join(', ');
      
      console.log('Lab summary for general LLM:', labSummary);
      
      if (labSummary && !userMessageLower.includes('hello') && !userMessageLower.includes('hi')) {
        try {
          console.log('Trying general LLM endpoint...');
          const llmResponse = await apiService.post('/api/llm_explanation', {
            marker: "Lab Discussion",
            value: userMessage,
            units: "Query",
            context: {
              labSummary,
              userQuery: userMessage
            }
          });
          
          console.log('General LLM response:', llmResponse);
          if (llmResponse.status === 'success' && llmResponse.data?.explanation) {
            console.log('General LLM success, returning:', llmResponse.data.explanation);
            return llmResponse.data.explanation;
          }
        } catch (llmError) {
          console.error('Public LLM endpoint also failed:', llmError);
        }
      }
    }

    // Analyze the user's question to determine what they're asking about
    const testData = context || {};
    
    // Build a comprehensive context string
    let contextString = '';
    const testEntries = Object.entries(testData);
    
    if (viewType === 'single' && selectedDate) {
      contextString = `Lab results from ${new Date(selectedDate).toLocaleDateString()}:\n\n`;
    } else {
      contextString = 'Lab test trends over time:\n\n';
    }
    
    // Check if asking about a specific test
    let specificTestFound = null;
    for (const [testName, testInfo] of testEntries) {
      const info = testInfo as any;
      const testNameLower = testName.toLowerCase();
      const displayName = info?.displayName || testName;
      const displayNameLower = displayName.toLowerCase();
      
      // Check for test name mentions
      if (userMessageLower.includes(testNameLower) || 
          userMessageLower.includes(displayNameLower) ||
          userMessageLower.includes(testNameLower.replace(/s$/, '')) ||
          userMessageLower.includes(displayNameLower.replace(/s$/, ''))) {
        specificTestFound = { testName, testInfo: info, displayName };
        break;
      }
    }
    
    // If asking about a specific test, get detailed explanation from LLM
    // BUT skip this if user is specifically asking about trends
    if (specificTestFound && !userMessageLower.includes('trend') && !userMessageLower.includes('over time') && !userMessageLower.includes('across all dates')) {
      const { testInfo, displayName } = specificTestFound;
      
      // Handle different data structures for single vs trends view
      let value, units, referenceMin, referenceMax;
      
      if (viewType === 'trends' && testInfo.dataPoints && testInfo.dataPoints.length > 0) {
        // For trends view, use the most recent data point
        const latestPoint = testInfo.dataPoints[testInfo.dataPoints.length - 1];
        value = latestPoint.value;
        units = latestPoint.units || testInfo.units;
        referenceMin = latestPoint.referenceMin;
        referenceMax = latestPoint.referenceMax;
      } else {
        // For single view or direct value
        value = testInfo.value;
        units = testInfo.units;
        referenceMin = testInfo.referenceMin;
        referenceMax = testInfo.referenceMax;
      }
      
      try {
        const llmResponse = await apiService.post('/api/llm_explanation', {
          marker: displayName,
          value: value,
          units: units
        });
        
        if (llmResponse.status === 'success' && llmResponse.data?.explanation) {
          let explanation = llmResponse.data.explanation;
          
          // Add reference range context if available
          if (referenceMin !== null && referenceMax !== null) {
            explanation += `\n\nReference range: ${referenceMin}-${referenceMax} ${units}`;
            
            if (typeof value === 'number') {
              if (value < referenceMin) {
                explanation += '\nYour value is below the normal range.';
              } else if (value > referenceMax) {
                explanation += '\nYour value is above the normal range.';
              } else {
                explanation += '\nYour value is within the normal range.';
              }
            }
          }
          
          return explanation;
        }
      } catch (llmError: any) {
        console.error('LLM explanation error:', llmError);
        
        // Check if it's a 400 error about invalid value
        if (llmError.response?.status === 400 && 
            llmError.response?.data?.message?.includes('not available')) {
          return `I can see you're asking about ${specificTestFound.displayName}, but unfortunately the value for this test appears to be missing or not available in your results. This could happen if the test wasn't performed or if there was an issue with the PDF extraction.`;
        }
      }
    }
    
    // Handle various types of questions
    if (userMessageLower.includes('what') && userMessageLower.includes('mean')) {
      // If asking what something means and we found a specific test
      if (specificTestFound) {
        const { testInfo, displayName } = specificTestFound;
        
        // Handle different data structures for single vs trends view
        let value, units;
        
        if (viewType === 'trends' && testInfo.dataPoints && testInfo.dataPoints.length > 0) {
          // For trends view, use the most recent data point
          const latestPoint = testInfo.dataPoints[testInfo.dataPoints.length - 1];
          value = latestPoint.value;
          units = latestPoint.units || testInfo.units;
        } else {
          // For single view or direct value
          value = testInfo.value;
          units = testInfo.units;
        }
        
        try {
          const llmResponse = await apiService.post('/api/llm_explanation', {
            marker: displayName,
            value: value,
            units: units
          });
          
          if (llmResponse.status === 'success' && llmResponse.data?.explanation) {
            return llmResponse.data.explanation;
          }
        } catch (llmError: any) {
          console.error('LLM explanation error:', llmError);
          
          // Check if it's a 400 error about invalid value
          if (llmError.response?.status === 400 && 
              llmError.response?.data?.message?.includes('not available')) {
            return `I can see you're asking about ${displayName}, but unfortunately the value for this test appears to be missing or not available in your results. This could happen if the test wasn't performed or if there was an issue with the PDF extraction.`;
          }
        }
      }
    }
    
    // If asking about trends or changes - provide detailed trend analysis
    if ((userMessageLower.includes('trend') || userMessageLower.includes('change') || 
         userMessageLower.includes('increase') || userMessageLower.includes('decrease') ||
         userMessageLower.includes('over time') || userMessageLower.includes('across all dates')) && 
         viewType === 'trends') {
      
      // Check if asking about a specific test's trends
      if (specificTestFound && userMessageLower.includes('trend')) {
        const { testInfo, displayName } = specificTestFound;
        if (testInfo.dataPoints && testInfo.dataPoints.length > 0) {
          let trendAnalysis = `Here's your ${displayName} trend analysis:\n\n`;
          
          // Show all data points
          trendAnalysis += `📊 Values over time:\n`;
          testInfo.dataPoints.forEach((dp: any) => {
            const date = new Date(dp.date).toLocaleDateString();
            const status = dp.value < dp.referenceMin ? '⬇️ Low' : 
                          dp.value > dp.referenceMax ? '⬆️ High' : '✓ Normal';
            trendAnalysis += `  • ${date}: ${dp.value} ${dp.units} ${status}\n`;
          });
          
          if (testInfo.dataPoints.length > 1) {
            // Calculate overall trend
            const firstValue = testInfo.dataPoints[0].value;
            const lastValue = testInfo.dataPoints[testInfo.dataPoints.length - 1].value;
            const change = lastValue - firstValue;
            const percentChange = ((change / firstValue) * 100).toFixed(1);
            
            trendAnalysis += `\n📈 Overall trend:\n`;
            if (Math.abs(change) < firstValue * 0.05) {
              trendAnalysis += `Your ${displayName} has remained relatively stable`;
            } else if (change > 0) {
              trendAnalysis += `Your ${displayName} has increased by ${Math.abs(change).toFixed(2)} ${testInfo.units} (${percentChange}%)`;
            } else {
              trendAnalysis += `Your ${displayName} has decreased by ${Math.abs(change).toFixed(2)} ${testInfo.units} (${percentChange.startsWith('-') ? percentChange.slice(1) : percentChange}%)`;
            }
            
            // Analyze pattern
            let increasing = 0, decreasing = 0;
            for (let i = 1; i < testInfo.dataPoints.length; i++) {
              if (testInfo.dataPoints[i].value > testInfo.dataPoints[i-1].value) increasing++;
              else if (testInfo.dataPoints[i].value < testInfo.dataPoints[i-1].value) decreasing++;
            }
            
            if (increasing > decreasing * 2) {
              trendAnalysis += ` with a generally increasing pattern.`;
            } else if (decreasing > increasing * 2) {
              trendAnalysis += ` with a generally decreasing pattern.`;
            } else {
              trendAnalysis += ` with some fluctuation.`;
            }
            
            // Reference range context
            trendAnalysis += `\n\n📋 Reference range: ${testInfo.dataPoints[0].referenceMin}-${testInfo.dataPoints[0].referenceMax} ${testInfo.units}`;
            
            // Add interpretation based on whether values are in range
            const outOfRangeCount = testInfo.dataPoints.filter((dp: any) => 
              dp.value < dp.referenceMin || dp.value > dp.referenceMax
            ).length;
            
            if (outOfRangeCount === 0) {
              trendAnalysis += `\n✅ All values have been within the normal range.`;
            } else if (outOfRangeCount === testInfo.dataPoints.length) {
              trendAnalysis += `\n⚠️ All values have been outside the normal range.`;
            } else {
              trendAnalysis += `\n⚠️ ${outOfRangeCount} out of ${testInfo.dataPoints.length} values were outside the normal range.`;
            }
          }
          
          return trendAnalysis;
        }
      }
      
      // General trends summary for all tests
      let trendSummary = 'Looking at your test trends:\n\n';
      
      for (const [testName, testInfo] of testEntries) {
        const info = testInfo as any;
        if (info.dataPoints && info.dataPoints.length > 1) {
          const firstValue = info.dataPoints[0].value;
          const lastValue = info.dataPoints[info.dataPoints.length - 1].value;
          const change = lastValue - firstValue;
          const percentChange = ((change / firstValue) * 100).toFixed(1);
          
          trendSummary += `${info.displayName || testName}: `;
          if (Math.abs(change) < firstValue * 0.05) {
            trendSummary += `Stable\n`;
          } else if (change > 0) {
            trendSummary += `↑ ${percentChange}%\n`;
          } else {
            trendSummary += `↓ ${percentChange.startsWith('-') ? percentChange.slice(1) : percentChange}%\n`;
          }
        }
      }
      
      trendSummary += '\n\n💡 Tip: For more detailed AI-powered trend analysis and recommendations, make sure you\'re logged in and ask specific questions about improvements or patterns.\n\nFor detailed trends of a specific test, ask me about that test\'s trends.';
      return trendSummary;
    }
    
    // If asking about overall results or no specific test found
    if (userMessageLower.includes('overall') || 
        userMessageLower.includes('summary') || 
        userMessageLower.includes('all') ||
        userMessageLower.includes('results') ||
        !specificTestFound) {
      
      // Build a summary of all tests
      let summary = contextString;
      const abnormalTests = [];
      const normalTests = [];
      
      for (const [testName, testInfo] of testEntries) {
        const info = testInfo as any;
        const displayName = info?.displayName || testName;
        
        // Handle different data structures for single vs trends view
        let value, units, refMin, refMax;
        
        if (viewType === 'trends' && info.dataPoints && info.dataPoints.length > 0) {
          // For trends view, use the most recent data point
          const latestPoint = info.dataPoints[info.dataPoints.length - 1];
          value = latestPoint.value;
          units = latestPoint.units || info.units;
          refMin = latestPoint.referenceMin;
          refMax = latestPoint.referenceMax;
        } else {
          // For single view or direct value
          value = info.value;
          units = info.units;
          refMin = info.referenceMin;
          refMax = info.referenceMax;
        }
        
        if (typeof value === 'number' && refMin !== null && refMax !== null) {
          if (value < refMin || value > refMax) {
            abnormalTests.push(`${displayName}: ${value} ${units} (Reference: ${refMin}-${refMax})`);
          } else {
            normalTests.push(`${displayName}: ${value} ${units}`);
          }
        } else {
          normalTests.push(`${displayName}: ${value} ${units}`);
        }
      }
      
      if (abnormalTests.length > 0) {
        summary += 'Tests outside normal range:\n';
        abnormalTests.forEach(test => summary += `• ${test}\n`);
        summary += '\n';
      }
      
      if (normalTests.length > 0) {
        summary += 'Tests within normal range:\n';
        normalTests.forEach(test => summary += `• ${test}\n`);
      }
      
      summary += '\n\nPlease discuss these results with your healthcare provider for proper interpretation and any necessary follow-up.';
      
      return summary;
    }
    
    // Fall back to local response for general questions
    console.log('All API attempts failed, falling back to local response...');
    console.log('Context passed to generateLocalResponse:', Object.keys(context || {}));
    console.log('UserMessage:', userMessage);
    console.log('ViewType:', viewType);
    const localResponse = generateLocalResponse(userMessage, context, viewType, selectedDate);
    console.log('Local response generated:', localResponse);
    console.log('Local response type:', typeof localResponse);
    console.log('Local response length:', localResponse?.length);
    
    // Ensure we always return a meaningful response
    if (localResponse && localResponse.trim()) {
      console.log('=== FINAL RESPONSE (LOCAL) ===:', localResponse);
      return localResponse;
    } else {
      const fallback = "I'm having trouble generating a detailed response right now. This might be due to a temporary issue with the AI services. Please try rephrasing your question or ask about a specific test value, and I'll do my best to help with the information I have available.";
      console.log('=== FINAL RESPONSE (FALLBACK) ===:', fallback);
      return fallback;
    }
    
  } catch (error) {
    console.error('Error generating intelligent response:', error);
    // Fall back to local response
    console.log('Exception caught, using fallback response...');
    const fallbackResponse = generateLocalResponse(userMessage, context, viewType, selectedDate);
    console.log('Fallback response generated:', fallbackResponse);
    
    if (fallbackResponse && fallbackResponse.trim()) {
      console.log('=== FINAL FALLBACK (LOCAL) ===:', fallbackResponse);
      return fallbackResponse;
    } else {
      const finalFallback = "I apologize, but I encountered an unexpected error while processing your request. This might be temporary - please try asking your question again. If the issue persists, try asking about a specific test value and I'll help with the available information.";
      console.log('=== FINAL FALLBACK (ERROR) ===:', finalFallback);
      return finalFallback;
    }
  }
}

// Local response generation for when API is not available
function generateLocalResponse(userMessage: string, context: any, viewType: string, selectedDate?: string): string {
  const userMessageLower = userMessage.toLowerCase();
  console.log('generateLocalResponse:', { 
    question: userMessage, 
    hasData: Object.keys(context || {}).length > 0,
    viewType 
  });
  
  // Check for greetings
  if (userMessage.match(/\b(hello|hi|hey|help)\b/)) {
    return "I'm here to help you understand your lab results! You can ask me about specific test values, what they mean, or general questions about your results. What would you like to know?";
  }
  
  // Check for specific test mentions
  const testData = context || {};
  
  for (const [testName, testInfo] of Object.entries(testData)) {
    // Check if user message contains the test name (case insensitive)
    const testNameLower = testName.toLowerCase();
    const displayName = (testInfo as any)?.displayName?.toLowerCase() || testNameLower;
    
    // Also check for common variations (e.g., "basophil" for "basophils")
    const testNameSingular = testNameLower.replace(/s$/, ''); // Remove trailing 's'
    const displayNameSingular = displayName.replace(/s$/, '');
    
    if (userMessage.includes(testNameLower) || 
        userMessage.includes(displayName) || 
        userMessage.includes(testNameSingular) ||
        userMessage.includes(displayNameSingular)) {
      if (typeof testInfo === 'object' && testInfo !== null) {
        const info = testInfo as any;
        // Handle different data structures for single vs trends view
        let value, units, refMin, refMax;
        
        if (viewType === 'trends' && info.dataPoints && info.dataPoints.length > 0) {
          // For trends view, use the most recent data point
          const latestPoint = info.dataPoints[info.dataPoints.length - 1];
          value = latestPoint.value ?? 'N/A';
          units = latestPoint.units || info.units || '';
          refMin = latestPoint.referenceMin ?? null;
          refMax = latestPoint.referenceMax ?? null;
        } else {
          // For single view or direct value
          value = info.value ?? 'N/A';
          units = info.units ?? '';
          refMin = info.referenceMin ?? null;
          refMax = info.referenceMax ?? null;
        }
        
        const displayName = info.displayName ?? testName;
        
        let response = `Your ${displayName} is ${value} ${units}. `;
        
        if (refMin !== null && refMax !== null) {
          response += `The normal range is typically ${refMin}-${refMax} ${units}. `;
          
          if (typeof value === 'number' && typeof refMin === 'number' && typeof refMax === 'number') {
            if (value < refMin) {
              response += "Your value appears to be below the normal range. ";
            } else if (value > refMax) {
              response += "Your value appears to be above the normal range. ";
            } else {
              response += "Your value is within the normal range. ";
            }
          }
        }
        
        response += "\n\nPlease consult with your healthcare provider for a complete interpretation of your results.";
        return response;
      }
    }
  }
  
  // Check for questions about improvement
  const improvementMatch = userMessage.match(/\b(improve|better|recommendation|advice|help)\b/i);
  console.log('Improvement match found:', !!improvementMatch, 'for message:', userMessage);
  
  if (improvementMatch) {
    const testCount = Object.keys(testData).length;
    console.log('Test count:', testCount, 'Test data keys:', Object.keys(testData));
    
    if (testCount > 0) {
      const response = "Based on your lab results, I can provide general wellness information. For personalized recommendations to improve your results, it's important to consult with your healthcare provider. They can consider your complete medical history and provide tailored advice. In general, maintaining a balanced diet, regular exercise, adequate sleep, and stress management can positively impact many lab values.";
      console.log('Returning improvement response with data:', response);
      return response;
    }
    const response = "To provide recommendations, I need to see your lab results first. Please make sure you have uploaded lab PDFs and selected tests to view. Once I can see your data, I can offer general wellness information, though personalized medical advice should always come from your healthcare provider.";
    console.log('Returning improvement response without data:', response);
    return response;
  }
  
  // Check for questions about what tests mean
  if (userMessage.match(/\b(what|mean|is)\b/)) {
    return "I can help explain what your lab values mean! Please ask about a specific test result, and I'll provide information about what that test measures and what the values typically indicate. Remember, always consult with your healthcare provider for medical advice.";
  }
  
  // Check for questions about normal ranges
  if (userMessage.match(/\b(normal|range)\b/)) {
    return "Each lab test has a reference range that indicates typical values for healthy individuals. Your results show whether your values fall within, above, or below these ranges. Green indicators mean within range, while red indicates outside the normal range. Your healthcare provider can best interpret what these mean for your specific situation.";
  }
  
  // Check for concerns
  if (userMessage.match(/\b(worried|concern|scared|afraid)\b/)) {
    return "I understand lab results can be concerning. While I can help explain what the tests measure, it's important to discuss any concerns with your healthcare provider. They have your complete medical history and can provide personalized interpretation and guidance.";
  }
  
  // Context-specific responses
  if (viewType === 'single' && selectedDate) {
    const testCount = Object.keys(testData).length;
    return `I'm looking at your ${testCount} test results from ${new Date(selectedDate).toLocaleDateString()}. Feel free to ask about any specific test value or what it means. I'm here to help you understand your results!`;
  }
  
  if (viewType === 'trends') {
    const testNames = Object.keys(testData).join(', ');
    return `I can see your trend data for: ${testNames}. Would you like to know about any specific test trends or what changes in these values might indicate? Remember to discuss any significant changes with your healthcare provider.`;
  }
  
  // Default response
  const defaultResponse = "I'd be happy to help you understand your lab results! You can ask me about specific test values, what they measure, or general questions about your results. What would you like to know?";
  console.log('Returning default response:', defaultResponse);
  return defaultResponse;
}